import type { CellValueChangedEvent, GridApi, GridReadyEvent } from "ag-grid-community"
import { useCallback, useRef, useState } from "react"

import { COL_HANDLERS } from "../components/tables/cellHandlers"
import { BLANK_PRICING_TIER } from "../components/tables/constants"
import { type PricingGridRow } from "../components/tables/types"
import { refreshRowsInPlace } from "../components/tables/utils"

import type { PriceChangeItemGroup, PriceChangeType } from "@/graphql/codegen/graphql"
import { PriceChangeTableVariant } from "@/screens/Pricing/constants"
import type { WarehouseHeaderFormValues } from "@/screens/Pricing/schema/WarehouseHeaderSchema"
import { usePriceTableEditsStore } from "@/screens/Pricing/stores/usePriceTableEditsStore"
import { pricingCalc } from "@/screens/Pricing/utils/pricingCalc"
import { getPriceChangeEditKey, getPricingRowKey } from "@/screens/Pricing/utils/pricingRow"
import type { toSurchargeRowUpdate } from "@/screens/Pricing/utils/surcharge"

/**
 * Context needed to persist an edit so it survives pagination: enough to rebuild
 * a SubmitPriceChangeItemInput and to rehydrate the grid when the page is revisited.
 * When omitted (readonly / review flows) persistence is disabled.
 */
export interface PricingEditContext {
  productId: number
  group: PriceChangeItemGroup
  uomId: string
  priceChangeType: PriceChangeType
  assignedToId?: string
}

type ApprovedTierFields = Pick<
  PricingGridRow,
  | "qty"
  | "surcharge1Price"
  | "surcharge1Description"
  | "surcharge2Price"
  | "surcharge2Description"
  | "surcharge3Price"
  | "surcharge3Description"
>

const toApprovedTierFields = (row: PricingGridRow): ApprovedTierFields => ({
  qty: row.qty,
  surcharge1Price: row.surcharge1Price,
  surcharge1Description: row.surcharge1Description,
  surcharge2Price: row.surcharge2Price,
  surcharge2Description: row.surcharge2Description,
  surcharge3Price: row.surcharge3Price,
  surcharge3Description: row.surcharge3Description,
})

interface UsePricingTiersActionsReturn {
  rowData: PricingGridRow[]
  rowDataRef: React.MutableRefObject<PricingGridRow[]>
  modifiedRowsRef: React.MutableRefObject<Set<string>>
  editingSurchargeRowIndex: number | null
  hasChanges: boolean
  handleGridReady: (event: GridReadyEvent<PricingGridRow>) => void
  handleCellValueChanged: (event: CellValueChangedEvent<PricingGridRow>) => void
  handleAddPriceTier: (rowIndex: number) => void
  handleClearPriceTier: (rowIndex: number) => void
  handleEditSurcharge: (rowIndex: number) => void
  handleSurchargeDialogClose: () => void
  handleSurchargeSubmit: (update: ReturnType<typeof toSurchargeRowUpdate>) => void
  isRowNew: (row: PricingGridRow) => boolean
  getSelectedChangedRowData: (isUomChanged: boolean) => PricingGridRow[]
  getAllChangedRowData: (isUomChanged: boolean) => PricingGridRow[]
  handleApplyPriceChange: (formValues: WarehouseHeaderFormValues) => void
  handleApplyGroupCostChange: (formValues: WarehouseHeaderFormValues) => void
  resetInvalidDates: () => void
  reseed: (rows: PricingGridRow[]) => void
  hasManualEditsInSelection: () => boolean
  hasAnyManualEdits: () => boolean
  syncUomRows: (isUomChanged: boolean) => void
  flushPendingEdits: () => Promise<void>
}

export const usePricingTiersActions = (
  initialRows: PricingGridRow[],
  variant: PriceChangeTableVariant,
  editContext?: PricingEditContext
): UsePricingTiersActionsReturn => {
  const [rowData, setRowData] = useState<PricingGridRow[]>(initialRows)
  const rowDataRef = useRef(rowData)
  rowDataRef.current = rowData

  const editContextRef = useRef(editContext)
  editContextRef.current = editContext

  // Only the SubmitPriceChange (Price Change screen) variant persists a whole PriceChangeItem's
  // tiers on any edit — ReviewSubmission/RevertSubmission (Review Submission screen) keep
  // persisting only the touched row(s), unchanged.
  const isSubmitPriceChange = variant === PriceChangeTableVariant.SubmitPriceChange

  const modifiedRowsRef = useRef<Set<string>>(new Set())
  const manuallyEditedRowsRef = useRef<Set<string>>(new Set())
  const originalTierFieldsRef = useRef<Map<string, ApprovedTierFields>>(
    new Map(initialRows.map((row) => [getPricingRowKey(row), toApprovedTierFields(row)]))
  )

  // Seeds modified/manual sets from persisted edits so a (re)seeded grid shows the
  // "modified" highlight and count for rows that already carry a persisted edit.
  const seedModifiedAndManualSets = useCallback((rows: PricingGridRow[]) => {
    modifiedRowsRef.current = new Set()
    manuallyEditedRowsRef.current = new Set()
    const context = editContextRef.current
    if (!context) return
    const persistedEdits = usePriceTableEditsStore.getState().edits
    for (const row of rows) {
      const persisted = persistedEdits[getPriceChangeEditKey(context.group.id, row)]
      if (!persisted) continue
      const key = getPricingRowKey(row)
      if (persisted.isModified) modifiedRowsRef.current.add(key)
      if (persisted.isManual) manuallyEditedRowsRef.current.add(key)
    }
  }, [])

  // Runs once for the lifetime of this grid — subsequent reseeds go through reseed() below.
  const seededRef = useRef(false)
  if (!seededRef.current) {
    seededRef.current = true
    seedModifiedAndManualSets(initialRows)
  }

  const gridApiRef = useRef<GridApi<PricingGridRow>>()
  const [editingSurchargeRowIndex, setEditingSurchargeRowIndex] = useState<number | null>(null)
  const [hasChanges, setHasChanges] = useState(() => modifiedRowsRef.current.size > 0)
  const syncHasChanges = useCallback(() => {
    setHasChanges(modifiedRowsRef.current.size > 0)
  }, [])

  const persistRows = useCallback((rows: PricingGridRow[]) => {
    const context = editContextRef.current
    if (!context || !rows.length) return
    usePriceTableEditsStore.getState().upsertEdits(
      rows.map((row) => ({
        row: { ...row },
        productId: context.productId,
        group: context.group,
        uomId: context.uomId,
        priceChangeType: context.priceChangeType,
        assignedToId: context.assignedToId,
        isManual: manuallyEditedRowsRef.current.has(getPricingRowKey(row)),
        isModified: modifiedRowsRef.current.has(getPricingRowKey(row)),
      }))
    )
  }, [])

  const persistModifiedFromGrid = useCallback(
    (api: GridApi<PricingGridRow>) => {
      if (!editContextRef.current) return
      const rows: PricingGridRow[] = []
      api.forEachNode((node) => {
        if (node.data && modifiedRowsRef.current.has(getPricingRowKey(node.data))) rows.push(node.data)
      })
      persistRows(rows)
    },
    [persistRows]
  )

  // SubmitPriceChange only: any edit to a tier means the whole PriceChangeItem (customer/ship-to)
  // it belongs to must be resubmitted together — persist every tier of that item, or prune the
  // item's edits if nothing in it changed. Rows may span multiple items (e.g. bulk apply actions),
  // so partition by item id and persist/prune each item independently.
  const syncItemEdits = useCallback(
    (rows: PricingGridRow[]) => {
      const context = editContextRef.current
      if (!context) return
      const rowsByItemId = new Map<string, PricingGridRow[]>()
      for (const row of rows) {
        const itemRows = rowsByItemId.get(row.item.priceChangeItemId) ?? []
        itemRows.push(row)
        rowsByItemId.set(row.item.priceChangeItemId, itemRows)
      }
      for (const itemRows of rowsByItemId.values()) {
        const hasModifiedRow = itemRows.some((row) => modifiedRowsRef.current.has(getPricingRowKey(row)))
        if (hasModifiedRow) {
          persistRows(itemRows)
        } else {
          usePriceTableEditsStore
            .getState()
            .removeEdits(itemRows.map((row) => getPriceChangeEditKey(context.group.id, row)))
        }
      }
    },
    [persistRows]
  )

  const handleGridReady = useCallback((event: GridReadyEvent<PricingGridRow>) => {
    gridApiRef.current = event.api
  }, [])

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<PricingGridRow>) => {
      const { rowIndex, column, api, node } = event
      if (rowIndex === null || !node.data) return

      const colId = column.getColId()
      const handler = COL_HANDLERS[colId]
      const rowKey = getPricingRowKey(node.data)

      if (handler) {
        handler({ api, node, rowKey, modifiedRows: modifiedRowsRef.current })
      }

      if (colId !== "selected") {
        modifiedRowsRef.current.add(rowKey)
        manuallyEditedRowsRef.current.add(rowKey)
        const context = editContextRef.current
        if (context) {
          // The date is shared by all tiers of the item.
          const itemKeys = rowDataRef.current
            .filter((row) => row.item.priceChangeItemId === node.data?.item.priceChangeItemId)
            .map((row) => getPriceChangeEditKey(context.group.id, row))
          usePriceTableEditsStore.getState().removeDefaultedRows(itemKeys)
        }
        refreshRowsInPlace([node])
        if (isSubmitPriceChange) {
          // AG Grid mutates row objects in place, so rowDataRef.current already reflects this edit.
          syncItemEdits(rowDataRef.current)
        } else {
          // Spanned handlers mark sibling rows modified too — persist every modified row.
          persistModifiedFromGrid(api)
        }
        syncHasChanges()
      }
    },
    [isSubmitPriceChange, persistModifiedFromGrid, syncItemEdits, syncHasChanges]
  )

  const handleAddPriceTier = useCallback(
    (rowIndex: number) => {
      if (rowIndex < 0) return
      const prev = rowDataRef.current
      const origin = prev[rowIndex]
      const added = { ...origin, id: crypto.randomUUID() }
      modifiedRowsRef.current.add(getPricingRowKey(added))
      const nextRows = [...prev]
      nextRows.splice(rowIndex + 1, 0, added)
      setRowData(nextRows)
      if (isSubmitPriceChange) {
        syncItemEdits(nextRows)
      } else {
        persistRows([added])
      }
      syncHasChanges()
    },
    [isSubmitPriceChange, persistRows, syncItemEdits, syncHasChanges]
  )

  const handleClearPriceTier = useCallback(
    (rowIndex: number) => {
      if (rowIndex < 0) return
      const prev = rowDataRef.current
      const row = prev[rowIndex]
      if (!row) return

      const rowKey = getPricingRowKey(row)
      const approved = originalTierFieldsRef.current.get(rowKey)
      let persistedRow: PricingGridRow | undefined
      let nextRows: PricingGridRow[]

      if (!approved) {
        modifiedRowsRef.current.delete(rowKey)
        manuallyEditedRowsRef.current.delete(rowKey)
        nextRows = prev.filter((_, i) => i !== rowIndex)
        const context = editContextRef.current
        if (context) {
          usePriceTableEditsStore.getState().removeEdits([getPriceChangeEditKey(context.group.id, row)])
        }
      } else if (variant === PriceChangeTableVariant.ReviewSubmission) {
        modifiedRowsRef.current.add(rowKey)
        manuallyEditedRowsRef.current.delete(rowKey)
        const newPrice = row.previousPrice ?? null
        const reverted = { ...row, ...approved, newPrice }
        persistedRow = reverted
        nextRows = prev.map((r, i) => (i === rowIndex ? reverted : r))
      } else {
        const cleared = { ...row, ...BLANK_PRICING_TIER }
        modifiedRowsRef.current.add(getPricingRowKey(cleared))
        persistedRow = cleared
        nextRows = prev.map((r, i) => (i === rowIndex ? cleared : r))
      }

      setRowData(nextRows)
      if (isSubmitPriceChange) {
        syncItemEdits(nextRows)
      } else if (persistedRow) {
        persistRows([persistedRow])
      }
      syncHasChanges()
    },
    [isSubmitPriceChange, persistRows, syncItemEdits, syncHasChanges, variant]
  )

  const handleEditSurcharge = useCallback((rowIndex: number) => {
    if (rowIndex < 0) return
    setEditingSurchargeRowIndex(rowIndex)
  }, [])

  const handleSurchargeDialogClose = useCallback(() => {
    setEditingSurchargeRowIndex(null)
  }, [])

  const handleSurchargeSubmit = useCallback(
    (update: ReturnType<typeof toSurchargeRowUpdate>) => {
      if (editingSurchargeRowIndex === null) return
      const index = editingSurchargeRowIndex
      let updatedRow: PricingGridRow | undefined
      const nextRows = rowDataRef.current.map((row, i) => {
        if (i !== index) return row
        const next = { ...row, ...update }
        modifiedRowsRef.current.add(getPricingRowKey(next))
        updatedRow = next
        return next
      })
      setRowData(nextRows)
      if (isSubmitPriceChange) {
        syncItemEdits(nextRows)
      } else if (updatedRow) {
        persistRows([updatedRow])
      }
      setEditingSurchargeRowIndex(null)
      syncHasChanges()
    },
    [editingSurchargeRowIndex, isSubmitPriceChange, persistRows, syncItemEdits, syncHasChanges]
  )

  const getSelectedChangedRowData = useCallback((isUomChanged: boolean): PricingGridRow[] => {
    const rows: PricingGridRow[] = []
    gridApiRef.current?.getSelectedNodes().forEach((node) => {
      if (!node.data) return
      const isModified = modifiedRowsRef.current.has(getPricingRowKey(node.data))
      if (isUomChanged || isModified) {
        rows.push(node.data)
      }
    })
    return rows
  }, [])

  const getAllChangedRowData = useCallback((isUomChanged: boolean): PricingGridRow[] => {
    const rows: PricingGridRow[] = []
    gridApiRef.current?.forEachNode((node) => {
      if (!node.data) return
      const isModified = modifiedRowsRef.current.has(getPricingRowKey(node.data))
      if (isUomChanged || isModified) {
        rows.push(node.data)
      }
    })
    return rows
  }, [])

  // Apply Price Change: selected rows only, price-only. Accepted cost is untouched here —
  // cost changes flow through Apply Cost Change (group-wide) instead.
  const handleApplyPriceChange = useCallback(
    (formValues: WarehouseHeaderFormValues) => {
      if (!gridApiRef.current) return
      const selectedItemIds = new Set(
        gridApiRef.current
          .getSelectedNodes()
          .map((n) => n.data?.item.priceChangeItemId)
          .filter((id): id is string => Boolean(id))
      )
      if (!selectedItemIds.size) return

      const hasPriceChange = (formValues.desiredPriceChange ?? null) !== null
      if (!hasPriceChange) return

      const changedRows: PricingGridRow[] = []
      const nextRows = rowDataRef.current.map((row) => {
        if (!selectedItemIds.has(row.item.priceChangeItemId)) return row

        const acceptedCost = row.acceptedCost ?? null
        const newPrice = pricingCalc.newPrice({
          currentPrice: row.previousPrice,
          currentAcceptedCost: acceptedCost,
          changeValue: formValues.desiredPriceChange,
          changeType: formValues.priceChangeMode,
        })
        const newProfit = pricingCalc.newProfit({ newPrice, acceptedCost })
        const newMargin = pricingCalc.newMargin({ newPrice, acceptedCost })

        const rowKey = getPricingRowKey(row)
        modifiedRowsRef.current.add(rowKey)
        manuallyEditedRowsRef.current.delete(rowKey)

        const nextRow = { ...row, newPrice, newProfit, newMargin }
        changedRows.push(nextRow)
        return nextRow
      })
      setRowData(nextRows)
      gridApiRef.current.redrawRows()
      if (isSubmitPriceChange) {
        syncItemEdits(nextRows)
      } else {
        persistRows(changedRows)
      }
      syncHasChanges()
    },
    [isSubmitPriceChange, persistRows, syncItemEdits, syncHasChanges]
  )

  // Apply Cost Change: all rows in the group, selection ignored. Sets the new accepted cost and
  // recomputes profit/margin; when Maintain margins is on, price is recomputed to hold the margin.
  const handleApplyGroupCostChange = useCallback(
    (formValues: WarehouseHeaderFormValues) => {
      if (!gridApiRef.current) return

      const maintainMargin = formValues.maintainMargin ?? false
      const changedRows: PricingGridRow[] = []
      const nextRows = rowDataRef.current.map((row) => {
        const currentAcceptedCost = row.previousAcceptedCost ?? null
        const hasAcceptedCostChange =
          (formValues.newAcceptedCost ?? null) !== null && formValues.newAcceptedCost !== currentAcceptedCost
        if (!hasAcceptedCostChange) return row

        const nextAcceptedCost = formValues.newAcceptedCost ?? currentAcceptedCost
        const newPrice = maintainMargin
          ? pricingCalc.maintainMarginPrice({
              currentPrice: row.previousPrice,
              currentAcceptedCost,
              newAcceptedCost: nextAcceptedCost,
            })
          : (row.previousPrice ?? null)
        const newProfit = pricingCalc.newProfit({ newPrice, acceptedCost: nextAcceptedCost })
        const newMargin = pricingCalc.newMargin({ newPrice, acceptedCost: nextAcceptedCost })

        const rowKey = getPricingRowKey(row)
        modifiedRowsRef.current.add(rowKey)
        manuallyEditedRowsRef.current.delete(rowKey)

        const nextRow = { ...row, acceptedCost: nextAcceptedCost, newPrice, newProfit, newMargin }
        changedRows.push(nextRow)
        return nextRow
      })
      setRowData(nextRows)
      gridApiRef.current.redrawRows()
      if (isSubmitPriceChange) {
        syncItemEdits(nextRows)
      } else {
        persistRows(changedRows)
      }
      syncHasChanges()
    },
    [isSubmitPriceChange, persistRows, syncItemEdits, syncHasChanges]
  )

  // Full reseed of this grid's local state to the given rows — matches what a fresh
  // mount would produce, without unmounting/remounting the grid (e.g. bulk update apply/clear).
  const reseed = useCallback(
    (rows: PricingGridRow[]) => {
      seedModifiedAndManualSets(rows)
      originalTierFieldsRef.current = new Map(rows.map((row) => [getPricingRowKey(row), toApprovedTierFields(row)]))
      setEditingSurchargeRowIndex(null)
      setRowData(rows)
      syncHasChanges()
      gridApiRef.current?.redrawRows()
    },
    [seedModifiedAndManualSets, syncHasChanges]
  )

  // Resyncs local rowData from the store's already-corrected persisted edits after
  // usePriceTableEditsStore.resetInvalidDates() runs, instead of remounting the grid.
  const resetInvalidDates = useCallback(() => {
    const context = editContextRef.current
    if (!context) return
    const persistedEdits = usePriceTableEditsStore.getState().edits
    let changed = false
    const nextRows = rowDataRef.current.map((row) => {
      const persistedItem = persistedEdits[getPriceChangeEditKey(context.group.id, row)]?.row.item
      if (!persistedItem || persistedItem === row.item) return row
      changed = true
      return { ...row, item: persistedItem }
    })
    if (!changed) return
    rowDataRef.current = nextRows
    setRowData(nextRows)
    gridApiRef.current?.redrawRows()
  }, [])

  const hasManualEditsInSelection = useCallback((): boolean => {
    const selectedKeys = (gridApiRef.current?.getSelectedNodes() ?? [])
      .map((n) => (n.data ? getPricingRowKey(n.data) : null))
      .filter((k): k is string => k !== null)
    return selectedKeys.some((key) => manuallyEditedRowsRef.current.has(key))
  }, [])

  const hasAnyManualEdits = useCallback((): boolean => {
    return manuallyEditedRowsRef.current.size > 0
  }, [])

  // A UoM change makes every row of the product submittable. Persist all rows while
  // changed; on revert, drop the rows that were only kept because of the UoM change.
  const syncUomRows = useCallback(
    (isUomChanged: boolean) => {
      const context = editContextRef.current
      if (!context) return
      if (isUomChanged) {
        persistRows(rowDataRef.current)
      } else {
        const keysToRemove = rowDataRef.current
          .filter((row) => !modifiedRowsRef.current.has(getPricingRowKey(row)))
          .map((row) => getPriceChangeEditKey(context.group.id, row))
        usePriceTableEditsStore.getState().removeEdits(keysToRemove)
      }
    },
    [persistRows]
  )

  const isRowNew = useCallback((row: PricingGridRow): boolean => {
    return !originalTierFieldsRef.current.has(getPricingRowKey(row))
  }, [])

  const flushPendingEdits = useCallback(async (): Promise<void> => {
    gridApiRef.current?.stopEditing()
    await new Promise<void>((resolve) => setTimeout(resolve, 0))
  }, [])

  return {
    rowData,
    rowDataRef,
    modifiedRowsRef,
    editingSurchargeRowIndex,
    hasChanges,
    handleGridReady,
    handleCellValueChanged,
    handleAddPriceTier,
    handleClearPriceTier,
    handleEditSurcharge,
    handleSurchargeDialogClose,
    handleSurchargeSubmit,
    isRowNew,
    getSelectedChangedRowData,
    getAllChangedRowData,
    handleApplyPriceChange,
    handleApplyGroupCostChange,
    resetInvalidDates,
    reseed,
    hasManualEditsInSelection,
    hasAnyManualEdits,
    syncUomRows,
    flushPendingEdits,
  }
}
